import Loading from "../../../../components/Loading";
import Modal from "../../../../components/Modal";
import Button from "../../../../components/Button";
import {
  usePimsRessources,
  useSelectionState,
  useFilterState,
  useImportLogic,
} from "./hooks";
import { FilterSection, EventList, ProjectList } from "./components";
import { PimsImportModalProps, PimsEvent, PimsProject } from "./types";

export default function PimsImportModal({
  isOpen,
  onClose,
  mode = "projects",
  eventId,
}: PimsImportModalProps) {
  const { data, loading, error } = usePimsRessources({ shouldFetch: isOpen });
  const {
    selectionState,
    setSelectedSingleEvent,
    toggleProjectExpansion,
    handleProjectSelection,
    handleEventSelection,
  } = useSelectionState();
  const {
    filterState,
    setSearchText,
    setStartDate,
    setEndDate,
    clearFilters,
    filterEvents,
    filterProjects,
  } = useFilterState();
  const { handleImport, isLoading } = useImportLogic();

  const handleImportClick = async () => {
    const success = await handleImport(
      mode,
      data,
      selectionState.selectedProjects,
      selectionState.selectedEvents,
      selectionState.selectedSingleEvent,
      eventId
    );

    if (success) {
      onClose();
    }
  };

  // Get events for a specific project
  const getProjectEvents = (projectId: string): PimsEvent[] => {
    return (
      (data?.pimsRessources?.events
        ?.filter((event) => event?.serieId === projectId)
        ?.filter(Boolean) as PimsEvent[]) || []
    );
  };

  // Get filtered data based on mode
  const getFilteredData = () => {
    if (!data?.pimsRessources) return { projects: [], events: [] };

    const allEvents = (data.pimsRessources.events?.filter(Boolean) ||
      []) as PimsEvent[];
    const allProjects = data.pimsRessources.series?.filter(Boolean) || [];

    const filteredEvents = filterEvents(allEvents);
    const filteredProjects = filterProjects(
      allProjects as PimsProject[],
      getProjectEvents
    );

    return { projects: filteredProjects, events: filteredEvents };
  };

  // Render content based on mode
  const renderContent = () => {
    const { projects, events } = getFilteredData();

    if (mode === "eventTickets") {
      return (
        <EventList
          events={events}
          selectedEvents={selectionState.selectedSingleEvent}
          onEventSelection={(eventId) => setSelectedSingleEvent(eventId)}
          selectionMode="radio"
          radioName="eventTicketsSelection"
        />
      );
    } else if (mode === "events") {
      return (
        <EventList
          events={events}
          selectedEvents={selectionState.selectedEvents}
          onEventSelection={handleEventSelection}
        />
      );
    } else {
      return (
        <ProjectList
          projects={projects}
          getProjectEvents={(projectId) =>
            filterEvents(getProjectEvents(projectId))
          }
          selectionState={selectionState}
          onProjectSelection={handleProjectSelection}
          onEventSelection={handleEventSelection}
          onToggleExpansion={toggleProjectExpansion}
        />
      );
    }
  };

  return (
    <Modal
      isModalOpen={isOpen}
      onClose={onClose}
      title={
        mode === "eventTickets"
          ? "Synchroniser la billetterie avec Pims"
          : mode === "events"
          ? "Importer des évènements depuis Pims"
          : "Importer des projets depuis Pims"
      }
      description={
        mode === "eventTickets"
          ? "Sélectionnez l'évènement Pims correspondant pour synchroniser la billetterie"
          : mode === "events"
          ? "Veuillez sélectionner les évènements à importer"
          : "Veuillez sélectionner les projets et évènements à importer"
      }
      className="w-[90vw] h-[90vh] overflow-y-auto custom-scrollbar flex flex-col"
    >
      {loading && (
        <div className="flex items-center justify-center h-full">
          <Loading />
        </div>
      )}
      {error && (
        <div className="flex items-center justify-center w-full h-full">
          <div className="text-accent mx-auto">
            Impossible d'importer les données provenant de Pims. Veuillez
            réessayer utlérieurement.
          </div>
        </div>
      )}

      {!loading && !error && (
        <FilterSection
          filterState={filterState}
          mode={mode}
          onSearchTextChange={setSearchText}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onClearFilters={clearFilters}
        />
      )}

      <div className="flex-1 overflow-y-auto">{renderContent()}</div>

      <div className="border-t border-border pt-4 mt-4 flex justify-end">
        <Button
          className="w-1/3"
          onClick={handleImportClick}
          isLoading={isLoading}
        >
          {mode === "eventTickets" ? "Synchroniser" : "Importer"}
        </Button>
      </div>
    </Modal>
  );
}
